/**
 * 工程文档的读入管线（把 migrate 框架真正接到实际加载路径上）。
 *
 * 为什么需要这一层：
 * 蓝图 §3 定了 schemaVersion 与迁移框架，但如果各处加载工程时直接
 * `JSON.parse(...) as CanvasDoc`，那框架就是摆设——老工程、损坏工程、
 * 未来版本工程都会被当成当前结构硬读，结果是静默读坏用户作品。
 *
 * 规则：**任何**从磁盘/网络/localStorage 读入的工程，都必须过 loadXxx()，
 * 不允许在业务代码里直接断言成文档类型。
 *
 * 顺序固定为 迁移 → 结构校验，不可颠倒：
 * 先校验会在老版本工程上报一堆假错；先迁移才能在校验前补齐新增必填字段。
 */

import { type CanvasDoc, validateCanvasDoc } from './canvas';
import { type DirectorProject, validateDirectorProject } from './director';
import { migrate, type DocKind, type Versioned } from './schema';

export type LoadOk<T> = { ok: true; doc: T; migrated: boolean; fromVersion: number };

/**
 * 加载失败原因。前四个直接沿用 migrate 的判定结果，
 * 不在本层改名字——改一次名就多一处两端对不上的可能。
 */
export type LoadFailReason =
  | 'FUTURE_VERSION'
  | 'MISSING_VERSION'
  | 'BROKEN_CHAIN'
  | 'NO_PATH'
  | 'INVALID_STRUCTURE';

export interface LoadFail {
  ok: false;
  /** 机器可读原因，供 UI 分支处理 */
  reason: LoadFailReason;
  /** 面向用户的完整可读说明 */
  message: string;
  /** 结构校验发现的全部问题（INVALID_STRUCTURE 时非空） */
  problems: string[];
}

export type LoadResult<T> = LoadOk<T> | LoadFail;

function load<T extends Versioned>(
  kind: DocKind,
  raw: unknown,
  validate: (doc: unknown) => string[],
): LoadResult<T> {
  const m = migrate<T>(kind, raw);

  if (!m.ok) {
    return {
      ok: false,
      reason: m.reason,
      message: m.message,
      problems: [],
    };
  }

  const problems = validate(m.value);
  if (problems.length > 0) {
    return {
      ok: false,
      reason: 'INVALID_STRUCTURE',
      message: `该${kind === 'canvas' ? '画布' : '导演台'}工程结构不完整（共 ${problems.length} 处问题），已拒绝加载以免继续损坏它。`,
      problems,
    };
  }

  return { ok: true, doc: m.value, migrated: m.migrated, fromVersion: m.from };
}

export function loadCanvasDocument(raw: unknown): LoadResult<CanvasDoc> {
  return load<CanvasDoc>('canvas', raw, validateCanvasDoc);
}

export function loadDirectorProject(raw: unknown): LoadResult<DirectorProject> {
  return load<DirectorProject>('directorProject', raw, validateDirectorProject);
}

/**
 * JSON 文本入口：解析失败也要变成可读的加载失败，而不是抛异常冒到 UI 上。
 * 用户面对的应该是"这个工程打不开，因为……"，不是控制台红字。
 */
export function parseAndLoad<T>(
  text: string,
  loader: (raw: unknown) => LoadResult<T>,
): LoadResult<T> {
  try {
    return loader(JSON.parse(text) as unknown);
  } catch (e) {
    return {
      ok: false,
      reason: 'NO_PATH',
      message: `文件不是合法的 JSON：${e instanceof Error ? e.message : String(e)}`,
      problems: [],
    };
  }
}
